/**
 * Transitions Component Library
 *
 * CSS and React-based animation utilities for smooth page and component transitions.
 * Provides consistent animation patterns throughout the application.
 *
 * Features:
 * - Page transitions (fade, slide, scale)
 * - Modal/Dialog animations
 * - List item stagger animations
 * - Skeleton to content transitions
 * - Micro-interactions
 *
 * Usage:
 * import { FadeIn, SlideUp, PageTransition } from './components/ui/Transitions';
 *
 * <FadeIn>
 *   <YourComponent />
 * </FadeIn>
 */

import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import PropTypes from 'prop-types';

// ========================================
// Animation Presets
// ========================================

export const ANIMATION_PRESETS = {
  // Fade animations
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
    duration: 200,
  },
  fadeOut: {
    from: { opacity: 1 },
    to: { opacity: 0 },
    duration: 150,
  },

  // Slide animations
  slideUp: {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    duration: 300,
  },
  slideDown: {
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    duration: 300,
  },
  slideLeft: {
    from: { opacity: 0, transform: 'translateX(20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
    duration: 300,
  },
  slideRight: {
    from: { opacity: 0, transform: 'translateX(-20px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
    duration: 300,
  },

  // Scale animations
  scaleIn: {
    from: { opacity: 0, transform: 'scale(0.95)' },
    to: { opacity: 1, transform: 'scale(1)' },
    duration: 200,
  },
  scaleOut: {
    from: { opacity: 1, transform: 'scale(1)' },
    to: { opacity: 0, transform: 'scale(0.95)' },
    duration: 150,
  },

  // Pop animation (for modals)
  pop: {
    from: { opacity: 0, transform: 'scale(0.9)' },
    to: { opacity: 1, transform: 'scale(1)' },
    duration: 250,
  },

  // Bounce animation
  bounceIn: {
    from: { opacity: 0, transform: 'scale(0.3)' },
    to: { opacity: 1, transform: 'scale(1)' },
    duration: 400,
    easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
};

// ========================================
// Easing Functions
// ========================================

export const EASINGS = {
  easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

// ========================================
// Base Transition Component
// ========================================

export const Transition = ({
  children,
  show = true,
  preset = 'fadeIn',
  duration,
  delay = 0,
  easing = 'easeOut',
  onEnter,
  onEntered,
  onExit,
  onExited,
  unmountOnExit = false,
  className = '',
  style = {},
}) => {
  const [state, setState] = useState(show ? 'entered' : 'exited');
  const [shouldRender, setShouldRender] = useState(show);
  const nodeRef = useRef(null);

  const animPreset = ANIMATION_PRESETS[preset] || ANIMATION_PRESETS.fadeIn;
  const animDuration = duration || animPreset.duration;
  const animEasing = animPreset.easing || EASINGS[easing];

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        setState('entering');
        onEnter?.();
      });

      const timer = setTimeout(() => {
        setState('entered');
        onEntered?.();
      }, animDuration + delay);

      return () => clearTimeout(timer);
    } else {
      setState('exiting');
      onExit?.();

      const timer = setTimeout(() => {
        setState('exited');
        onExited?.();
        if (unmountOnExit) {
          setShouldRender(false);
        }
      }, animDuration);

      return () => clearTimeout(timer);
    }
  }, [show, animDuration, delay, unmountOnExit, onEnter, onEntered, onExit, onExited]);

  if (!shouldRender) return null;

  const isEntering = state === 'entering' || state === 'entered';
  const animStyles = isEntering ? animPreset.to : animPreset.from;

  return (
    <div
      ref={nodeRef}
      className={className}
      style={{
        ...style,
        ...animStyles,
        transition: `all ${animDuration}ms ${animEasing}`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

Transition.propTypes = {
  children: PropTypes.node.isRequired,
  show: PropTypes.bool,
  preset: PropTypes.string,
  duration: PropTypes.number,
  delay: PropTypes.number,
  easing: PropTypes.string,
  onEnter: PropTypes.func,
  onEntered: PropTypes.func,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  unmountOnExit: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

// ========================================
// Preset Transition Components
// ========================================

export const FadeIn = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="fadeIn"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const SlideUp = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="slideUp"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const SlideDown = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="slideDown"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const SlideLeft = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="slideLeft"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const SlideRight = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="slideRight"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const ScaleIn = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="scaleIn"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const Pop = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="pop"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

export const BounceIn = ({ children, delay = 0, duration, className = '', ...props }) => (
  <Transition
    preset="bounceIn"
    delay={delay}
    duration={duration}
    className={className}
    {...props}
  >
    {children}
  </Transition>
);

// ========================================
// Page Transition Component
// ========================================

export const PageTransition = ({
  children,
  className = '',
  delay = 0,
}) => {
  return (
    <SlideUp delay={delay} className={className}>
      {children}
    </SlideUp>
  );
};

PageTransition.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  delay: PropTypes.number,
};

// ========================================
// Stagger Container & Items
// ========================================

const StaggerContext = createContext({ staggerDelay: 50, baseDelay: 0 });

export const StaggerContainer = ({
  children,
  staggerDelay = 50,
  baseDelay = 0,
  className = '',
}) => {
  return (
    <StaggerContext.Provider value={{ staggerDelay, baseDelay }}>
      <div className={className}>{children}</div>
    </StaggerContext.Provider>
  );
};

StaggerContainer.propTypes = {
  children: PropTypes.node.isRequired,
  staggerDelay: PropTypes.number,
  baseDelay: PropTypes.number,
  className: PropTypes.string,
};

export const StaggerItem = ({
  children,
  index = 0,
  preset = 'slideUp',
  className = '',
}) => {
  const { staggerDelay, baseDelay } = useContext(StaggerContext);
  const delay = baseDelay + (index * staggerDelay);

  return (
    <Transition preset={preset} delay={delay} className={className}>
      {children}
    </Transition>
  );
};

StaggerItem.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number,
  preset: PropTypes.string,
  className: PropTypes.string,
};

// ========================================
// List Transition
// ========================================

export const AnimatedList = ({
  items,
  renderItem,
  keyExtractor,
  staggerDelay = 50,
  baseDelay = 0,
  preset = 'slideUp',
  className = '',
  itemClassName = '',
}) => {
  return (
    <StaggerContainer staggerDelay={staggerDelay} baseDelay={baseDelay} className={className}>
      {items.map((item, index) => (
        <StaggerItem
          key={keyExtractor ? keyExtractor(item, index) : index}
          index={index}
          preset={preset}
          className={itemClassName}
        >
          {renderItem(item, index)}
        </StaggerItem>
      ))}
    </StaggerContainer>
  );
};

AnimatedList.propTypes = {
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  keyExtractor: PropTypes.func,
  staggerDelay: PropTypes.number,
  baseDelay: PropTypes.number,
  preset: PropTypes.string,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
};

// ========================================
// Modal/Dialog Transitions
// ========================================

export const ModalTransition = ({
  children,
  show,
  onExited,
}) => {
  return (
    <Transition
      show={show}
      preset="pop"
      unmountOnExit
      onExited={onExited}
    >
      {children}
    </Transition>
  );
};

ModalTransition.propTypes = {
  children: PropTypes.node.isRequired,
  show: PropTypes.bool.isRequired,
  onExited: PropTypes.func,
};

export const OverlayTransition = ({
  children,
  show,
  onExited,
}) => {
  return (
    <Transition
      show={show}
      preset="fadeIn"
      duration={200}
      unmountOnExit
      onExited={onExited}
    >
      {children}
    </Transition>
  );
};

OverlayTransition.propTypes = {
  children: PropTypes.node.isRequired,
  show: PropTypes.bool.isRequired,
  onExited: PropTypes.func,
};

// ========================================
// Collapse Transition
// ========================================

export const Collapse = ({
  children,
  show,
  className = '',
}) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (show && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [show, children]);

  return (
    <div
      className={`overflow-hidden transition-all duration-300 ${EASINGS.easeOut} ${className}`}
      style={{ height: show ? height : 0 }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

Collapse.propTypes = {
  children: PropTypes.node.isRequired,
  show: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

// ========================================
// Skeleton to Content Transition
// ========================================

export const SkeletonTransition = ({
  loading,
  skeleton,
  children,
  className = '',
}) => {
  return (
    <div className={`relative ${className}`}>
      <Transition show={loading} preset="fadeIn" unmountOnExit>
        {skeleton}
      </Transition>
      <Transition show={!loading} preset="fadeIn" delay={100}>
        {children}
      </Transition>
    </div>
  );
};

SkeletonTransition.propTypes = {
  loading: PropTypes.bool.isRequired,
  skeleton: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// ========================================
// Animated Counter
// ========================================

export const AnimatedCounter = ({
  value,
  duration = 1000,
  className = '',
  formatter = (v) => Math.round(v).toLocaleString(),
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(0);

  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const newValue = startValueRef.current + (value - startValueRef.current) * eased;

      setDisplayValue(newValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span className={className}>
      {formatter(displayValue)}
    </span>
  );
};

AnimatedCounter.propTypes = {
  value: PropTypes.number.isRequired,
  duration: PropTypes.number,
  className: PropTypes.string,
  formatter: PropTypes.func,
};

// ========================================
// Pulse Animation (for notifications)
// ========================================

export const Pulse = ({
  children,
  active = true,
  color = 'purple',
  className = '',
}) => {
  const colorClasses = {
    purple: 'bg-primary-500',
    blue: 'bg-secondary-500',
    green: 'bg-success-500',
    red: 'bg-error-500',
    orange: 'bg-orange-500',
  };

  return (
    <span className={`relative inline-flex ${className}`}>
      {children}
      {active && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colorClasses[color]} opacity-75`} />
          <span className={`relative inline-flex rounded-full h-3 w-3 ${colorClasses[color]}`} />
        </span>
      )}
    </span>
  );
};

Pulse.propTypes = {
  children: PropTypes.node.isRequired,
  active: PropTypes.bool,
  color: PropTypes.oneOf(['purple', 'blue', 'green', 'red', 'orange']),
  className: PropTypes.string,
};

// ========================================
// CSS Keyframe Animations (inject once)
// ========================================

const injectAnimationStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('portfolistic-animations')) return;

  const style = document.createElement('style');
  style.id = 'portfolistic-animations';
  style.textContent = `
    @keyframes portfolistic-shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }

    @keyframes portfolistic-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    @keyframes portfolistic-bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }

    @keyframes portfolistic-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @keyframes portfolistic-slide-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes portfolistic-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .animate-shimmer {
      animation: portfolistic-shimmer 2s infinite linear;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%);
      background-size: 200% 100%;
    }

    .animate-portfolistic-pulse {
      animation: portfolistic-pulse 2s infinite;
    }

    .animate-portfolistic-bounce {
      animation: portfolistic-bounce 1s infinite;
    }

    .animate-slide-up {
      animation: portfolistic-slide-up 0.3s ease-out forwards;
    }

    .animate-fade-in {
      animation: portfolistic-fade-in 0.2s ease-out forwards;
    }
  `;
  document.head.appendChild(style);
};

// Inject styles on module load
if (typeof window !== 'undefined') {
  injectAnimationStyles();
}

// ========================================
// Export Default
// ========================================

export default Transition;
